<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import PageHeader from 'dashboard/components-next/page-header/PageHeader.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import LeadsAPI from 'dashboard/api/leads';
import LeadCard from '../components/LeadCard.vue';
import PipelineWidgets from '../components/PipelineWidgets.vue';
import CloseDropZones from '../components/CloseDropZones.vue';
import CloseLeadDialog from '../components/CloseLeadDialog.vue';
import StageTypeDialog from '../components/StageTypeDialog.vue';
import LossReasonsDialog from '../components/LossReasonsDialog.vue';
import {
  unwrap,
  formatLeadCurrency,
  downloadBlob,
  periodDateRange,
  PERIOD_OPTIONS,
  LEADS_PER_PAGE,
  waitingSince,
  WAITING_TONE_CLASSES,
} from '../helper';
import {
  isClosedType,
  stageTypeIcon,
  orderStages,
  groupedStages,
} from '../stageTypes';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { updateUISettings, uiSettings } = useUISettings();

const isLoading = ref(true);
const pipeline = ref(null);
const pipelines = ref([]);
// TODAS las etapas del pipeline (fuente de verdad: menús de mover, picker,
// grupos Embudo/Cierre). `columns` es el SUBCONJUNTO renderizado como columnas.
const stages = ref([]);
const columns = ref([]); // [{ id, stage, leads: [] }]

const isExporting = ref(false);
const archivedView = ref(false); // tablero de archivados (solo admin)

// Móvil: se muestra UNA etapa a la vez (chips arriba). Mover = botón explícito.
const activeStageId = ref(null);
const moveDialog = ref(null);
const leadToMove = ref(null);

// ¿El usuario ve TODOS los leads? (admin / agente sin rol restrictivo). Si no,
// se oculta el filtro por agente (solo vería los suyos). Espeja LeadPolicy.
const accountPermissions = computed(
  () =>
    getUserPermissions(
      store.getters.getCurrentUser,
      Number(route.params.accountId)
    ) || []
);
const hasFullLeadAccess = computed(() => {
  const perms = accountPermissions.value;
  if (perms.includes('administrator') || perms.includes('conversation_manage'))
    return true;
  return !(
    perms.includes('conversation_participating_manage') ||
    perms.includes('conversation_unassigned_manage')
  );
});

// Selector + gestión de pipelines (multi-pipeline).
const showPipelineMenu = ref(false);
const pipelineDialog = ref(null);
const deletePipelineDialog = ref(null);
const pipelineDialogMode = ref('create'); // 'create' | 'rename'
const pipelineNameInput = ref('');

const deleteStageDialog = ref(null);
const stageToDelete = ref(null);
const stageTypeDialog = ref(null);
const lossReasonsDialog = ref(null);
const openStageTypeDialog = stage => stageTypeDialog.value.open(stage);
const editingStageId = ref(null);
const editingStageName = ref('');

// Filtros del tablero (búsqueda + agente + estado). En móvil se colapsan.
// Los filtros del tablero viven TAMBIÉN en la query de la URL: volver de la
// ficha (router.back), F5 y un link compartido restauran la vista exacta.
// Se inicializan desde route.query AL DECLARAR (antes de registrar watchers →
// no dispara recargas dobles); el watcher de abajo los espeja con replace.
const qs = key => String(route.query[key] ?? '');
// assignee viaja como string en la URL pero los options usan id numérico.
const qsAssignee = () => {
  const raw = qs('assignee');
  if (!raw || raw === 'unassigned') return raw;
  const id = Number(raw);
  return Number.isNaN(id) ? '' : id;
};
const search = ref(qs('q'));
const assigneeFilter = ref(qsAssignee());
const statusFilter = ref(qs('status'));
// Filtros seteados al navegar desde un widget (rango de creación + sin-tarea).
const createdFrom = ref(qs('created_from'));
const createdTo = ref(qs('created_to'));
const hasPendingTask = ref(qs('pending_task'));
// Periodo elegido al navegar desde un widget (solo para rotular el chip).
const createdPeriod = ref(qs('created_period'));
// Rango de GANADO (won_at) al navegar desde la card "Ganado": la card cuenta
// por fecha de cierre — filtrar por fecha de creación mostraría una columna
// vacía para una venta ganada hoy de un lead viejo.
const wonFrom = ref(qs('won_from'));
const wonTo = ref(qs('won_to'));
const wonPeriod = ref(qs('won_period'));
// Periodo de los widgets (el selector vive en el header, visible solo con widgets).
const period = ref('today');
const filtersDialog = ref(null); // modal de filtros en móvil

const accountId = computed(() => route.params.accountId);

const agents = computed(() => store.getters['agents/getAgents'] || []);

const assigneeOptions = computed(() => [
  { value: '', label: t('LEADS.FILTER.ALL_AGENTS') },
  { value: 'unassigned', label: t('LEADS.FILTER.UNASSIGNED') },
  ...agents.value.map(agent => ({ value: agent.id, label: agent.name })),
]);

const statusOptions = computed(() => [
  { value: '', label: t('LEADS.FILTER.ALL_STATUS') },
  { value: 'open', label: t('LEADS.FILTER.STATUS_OPEN') },
  { value: 'won', label: t('LEADS.FILTER.STATUS_WON') },
  { value: 'lost', label: t('LEADS.FILTER.STATUS_LOST') },
]);

// TODOS los filtros cuentan, incluidos los que setean los widgets (rango de
// creación + sin-tarea): si un filtro puede achicar el tablero, "Limpiar" y
// los indicadores tienen que conocerlo — si no, el tablero "pierde leads".
const hasActiveFilters = computed(
  () =>
    !!search.value ||
    !!assigneeFilter.value ||
    !!statusFilter.value ||
    !!createdFrom.value ||
    !!wonFrom.value ||
    !!hasPendingTask.value
);
const hasNonSearchFilters = computed(
  () =>
    !!assigneeFilter.value ||
    !!statusFilter.value ||
    !!createdFrom.value ||
    !!wonFrom.value ||
    !!hasPendingTask.value
);

// --- Panel de widgets (el toggle "Resumen" vive en el header principal) ---
const showWidgets = computed(
  () => uiSettings.value.show_pipeline_widgets !== false
);
const toggleWidgets = () =>
  updateUISettings({ show_pipeline_widgets: !showWidgets.value });
const widgetsVisible = computed(() => !archivedView.value && showWidgets.value);

// --- Etapas de cierre fuera del tablero (paridad Kommo) ---
// Las columnas won/lost NO se renderizan por default; se revelan por:
// (1) filtro de estado Ganados/Perdidos, (2) búsqueda (encontrar al cliente
// perdido que volvió y arrastrarlo de vuelta), (3) toggle per-usuario.
const showClosed = computed(() => !!uiSettings.value.leads_show_closed);
const toggleShowClosed = () =>
  updateUISettings({ leads_show_closed: !showClosed.value });
const isStageRevealed = stage => {
  if (!isClosedType(stage.stage_type)) return true;
  if (showClosed.value) return true;
  if (statusFilter.value === stage.stage_type) return true;
  return !!search.value;
};
const visibleStages = computed(() => stages.value.filter(isStageRevealed));
const hasClosedStages = computed(() =>
  stages.value.some(s => isClosedType(s.stage_type))
);
// Cerradas visibles SOLO por la búsqueda (chip contextual: sin él, columnas
// que aparecen y desaparecen al tipear parecen un glitch).
const closedRevealedBySearch = computed(
  () =>
    !!search.value &&
    !showClosed.value &&
    statusFilter.value !== 'won' &&
    statusFilter.value !== 'lost' &&
    hasClosedStages.value
);
// Filtro de estado apuntando a un tipo de cierre que el pipeline NO tiene →
// avisar (el tablero vacío sin explicación parece un bug).
const missingClosedStageType = computed(() => {
  const type = statusFilter.value;
  if (type !== 'won' && type !== 'lost') return null;
  return stages.value.some(s => s.stage_type === type) ? null : type;
});

// Copy del vacío según contexto: con filtro de estado los leads EXISTEN (están
// filtrados) y el genérico "Sin leads en esta etapa" mentiría; las cerradas
// vacías tienen su propio copy.
const emptyColumnLabel = column => {
  if (statusFilter.value) {
    const option = statusOptions.value.find(
      o => o.value === statusFilter.value
    );
    return t('LEADS.EMPTY_WITH_STATUS', { status: option?.label || '' });
  }
  if (isClosedType(column.stage.stage_type)) {
    return column.stage.stage_type === 'won'
      ? t('LEADS.CLOSED.EMPTY_WON')
      : t('LEADS.CLOSED.EMPTY_LOST');
  }
  return t('LEADS.EMPTY_STAGE');
};

// Chips removibles de los filtros activos (arriba del tablero). Con el panel
// de widgets A LA VISTA, la card seleccionada (con su ✕) ES la señal de sus
// filtros → esos chips serían redundantes y se suprimen; quedan los que no
// tienen card (agente, estado abierto/perdido). Panel oculto o vista
// archivados → chips de TODO (el tablero nunca filtra en silencio).
// Agente filtrado como objeto (avatar + nombre para el chip destacado).
// 'unassigned' se representa sin avatar.
const filteredAgent = computed(() => {
  if (!assigneeFilter.value) return null;
  if (assigneeFilter.value === 'unassigned') {
    return { name: t('LEADS.FILTER.UNASSIGNED'), thumbnail: '' };
  }
  const agent = agents.value.find(a => a.id === assigneeFilter.value);
  return agent
    ? { name: agent.name, thumbnail: agent.thumbnail || '' }
    : { name: '', thumbnail: '' };
});

const activeFilterChips = computed(() => {
  const chips = [];
  const wonOnCard = widgetsVisible.value && statusFilter.value === 'won';
  if (statusFilter.value && !wonOnCard) {
    const option = statusOptions.value.find(
      o => o.value === statusFilter.value
    );
    chips.push({
      key: 'status',
      label: t('LEADS.FILTER.CHIP_STATUS', { status: option?.label || '' }),
    });
  }
  if (wonFrom.value && !wonOnCard) {
    const periodLabel = wonPeriod.value
      ? t(`LEADS.WIDGETS.PERIOD.${wonPeriod.value.toUpperCase()}`)
      : '';
    chips.push({
      key: 'won-range',
      label: t('LEADS.FILTER.CHIP_WON', { period: periodLabel }),
    });
  }
  if (createdFrom.value && !widgetsVisible.value) {
    const periodLabel = createdPeriod.value
      ? t(`LEADS.WIDGETS.PERIOD.${createdPeriod.value.toUpperCase()}`)
      : '';
    chips.push({
      key: 'created',
      label: t('LEADS.FILTER.CHIP_CREATED', { period: periodLabel }),
    });
  }
  if (hasPendingTask.value === 'false' && !widgetsVisible.value) {
    chips.push({ key: 'no-task', label: t('LEADS.FILTER.CHIP_NO_TASK') });
  }
  return chips;
});

const removeFilterChip = key => {
  if (key === 'assignee') assigneeFilter.value = '';
  else if (key === 'status') statusFilter.value = '';
  else if (key === 'no-task') hasPendingTask.value = '';
  else if (key === 'created') {
    createdFrom.value = '';
    createdTo.value = '';
    createdPeriod.value = '';
  } else if (key === 'won-range') {
    wonFrom.value = '';
    wonTo.value = '';
    wonPeriod.value = '';
  }
};

// --- Orden por columna (per-usuario, ui_settings) ---
// 'manual' = position (drag) · 'recent'/'oldest' = fecha de creación,
// ordenado SERVER-SIDE (con paginación, ordenar los 25 cargados mentiría).
// Default: la etapa de entrada muestra lo más nuevo primero (es una cola;
// con orden manual el lead nuevo entra al FONDO y con paginación queda
// invisible); el resto de etapas, manual.
const stageSortSettings = computed(
  () => uiSettings.value.leads_stage_sort || {}
);
const defaultSortForStage = stage => {
  if (isClosedType(stage.stage_type)) return 'closed_recent';
  return stage.stage_type === 'incoming' ? 'recent' : 'manual';
};
const sortForColumn = column =>
  stageSortSettings.value[column.stage.id] || defaultSortForStage(column.stage);

// Re-acomoda localmente una columna ordenada por fecha (tras soltar una card
// en ella): created_at/won_at/lost_at viajan como epoch (segundos).
// 'closed_recent' = fecha de CIERRE descendente (sin él, el lead recién
// cerrado caería al fondo de la paginación y "desaparecería").
const resortColumn = column => {
  const mode = sortForColumn(column);
  if (mode === 'manual') return;
  if (mode === 'closed_recent') {
    column.leads.sort(
      (a, b) => (b.won_at || b.lost_at || 0) - (a.won_at || a.lost_at || 0)
    );
    return;
  }
  const dir = mode === 'recent' ? -1 : 1;
  column.leads.sort((a, b) => dir * (a.created_at - b.created_at));
};

// Menú del selector: sección 1 = cambiar de pipeline; sección 2 (admin) = gestión.
const pipelineMenuSections = computed(() => {
  const sections = [
    {
      items: pipelines.value.map(p => ({
        label: p.name,
        action: 'switch',
        value: p.id,
        icon: p.is_default ? 'i-lucide-star' : 'i-lucide-kanban',
      })),
    },
  ];
  // Exportar visible para todos (antes vivía escondido en el modal "Filtros",
  // donde nadie lo busca; ahí queda también, para el flujo móvil).
  // "Mostrar cerradas" = vista per-usuario (todos los roles); deshabilitado con
  // hint si el pipeline no tiene etapas de cierre.
  sections.push({
    items: [
      {
        label: t('LEADS.EXPORT.LABEL'),
        action: 'export',
        value: 'export',
        icon: 'i-lucide-download',
      },
      {
        label: showClosed.value
          ? t('LEADS.CLOSED.HIDE_TOGGLE')
          : t('LEADS.CLOSED.SHOW_TOGGLE'),
        action: 'toggle-closed',
        value: 'toggle-closed',
        icon: showClosed.value ? 'i-lucide-eye-off' : 'i-lucide-trophy',
        disabled: !hasClosedStages.value,
      },
    ],
  });
  if (isAdmin.value) {
    const current = pipeline.value;
    const adminItems = [
      {
        label: t('LEADS.PIPELINE.NEW'),
        action: 'create',
        value: 'create',
        icon: 'i-lucide-plus',
      },
      {
        label: t('LEADS.PIPELINE.RENAME'),
        action: 'rename',
        value: 'rename',
        icon: 'i-lucide-pencil',
      },
      {
        label: t('LEADS.LOSS_REASONS.MENU'),
        action: 'loss-reasons',
        value: 'loss-reasons',
        icon: 'i-lucide-list-x',
      },
    ];
    if (current && !current.is_default) {
      adminItems.push({
        label: t('LEADS.PIPELINE.SET_DEFAULT'),
        action: 'set-default',
        value: 'set-default',
        icon: 'i-lucide-star',
      });
    }
    if (current && !current.is_default && pipelines.value.length > 1) {
      adminItems.push({
        label: t('LEADS.PIPELINE.DELETE'),
        action: 'delete',
        value: 'delete',
        icon: 'i-lucide-trash-2',
      });
    }
    adminItems.push({
      label: archivedView.value
        ? t('LEADS.PIPELINE.VIEW_ACTIVE')
        : t('LEADS.PIPELINE.VIEW_ARCHIVED'),
      action: 'toggle-archived',
      value: 'toggle-archived',
      icon: archivedView.value ? 'i-lucide-list' : 'i-lucide-archive',
    });
    sections.push({ items: adminItems });
  }
  return sections;
});

const leadQueryParams = computed(() => ({
  pipeline_id: pipeline.value?.id,
  search: search.value,
  assignee_id: assigneeFilter.value,
  status: statusFilter.value,
  created_from: createdFrom.value,
  created_to: createdTo.value,
  won_from: wonFrom.value,
  won_to: wonTo.value,
  has_pending_task: hasPendingTask.value,
  archived: archivedView.value ? 'true' : '',
}));

// Pipeline a mostrar: el último elegido por el usuario (ui_settings) si sigue
// existiendo, si no el default, si no el primero.
const pickPipeline = () => {
  const lastId = uiSettings.value?.last_pipeline_id;
  return (
    pipelines.value.find(p => p.id === lastId) ||
    pipelines.value.find(p => p.is_default) ||
    pipelines.value[0] ||
    null
  );
};

// Carga (o recarga) la primera página de una columna; o la siguiente si !reset.
const loadColumnLeads = async (column, reset = false) => {
  const page = reset ? 1 : column.page + 1;
  const sort = sortForColumn(column);
  column.loadingMore = true;
  try {
    const { data } = await LeadsAPI.get({
      ...leadQueryParams.value,
      stage_id: column.stage.id,
      page,
      ...(sort === 'manual' ? {} : { sort }),
    });
    const leads = unwrap(data);
    column.leads = reset ? leads : [...column.leads, ...leads];
    column.page = page;
    column.hasMore = leads.length === LEADS_PER_PAGE;
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    column.loadingMore = false;
  }
};

// Trae conteo + suma por etapa (server-side) y los vuelca en las columnas.
// El mapa completo se conserva (statsByStage): las etapas SIN columna (cerradas
// ocultas) alimentan el chip de descubrimiento "N leads en etapas de cierre".
const statsByStage = ref({});
const applyStats = async () => {
  const { data } = await LeadsAPI.stats(leadQueryParams.value);
  const byStage = {};
  unwrap(data).forEach(s => {
    byStage[s.stage_id] = s;
  });
  statsByStage.value = byStage;
  columns.value.forEach(column => {
    column.count = byStage[column.stage.id]?.count || 0;
    column.valueSum = byStage[column.stage.id]?.value_sum || 0;
    column.oldestAt = byStage[column.stage.id]?.oldest_at || null;
  });
};

// Leads sentados en etapas cerradas NO reveladas (para el chip de
// descubrimiento — sin él, el día que las cerradas salen del tablero los
// leads "desaparecen" sin explicación y el toggle queda enterrado).
const hiddenClosedCount = computed(() =>
  stages.value
    .filter(s => isClosedType(s.stage_type) && !isStageRevealed(s))
    .reduce((sum, s) => sum + (statsByStage.value[s.id]?.count || 0), 0)
);

// Única fábrica de columnas: buildColumns y addStage DEBEN crear la misma
// estructura (una columna sin count/valueSum numéricos rinde "NaN" en el header
// al recibir su primer lead).
const makeColumn = stage => ({
  id: stage.id,
  stage,
  leads: [],
  count: 0,
  valueSum: 0,
  oldestAt: null,
  page: 0,
  hasMore: false,
  loadingMore: false,
});

// Reconcilia `columns` con las etapas REVELADAS (visibleStages), preservando el
// estado de las columnas que siguen visibles (leads cargados, paginación) y
// creando columnas frescas para las recién reveladas. ÚNICO camino de creación
// de columnas (buildColumns, reloadLeads y el toggle pasan por acá).
const syncColumns = () => {
  const existing = new Map(columns.value.map(c => [c.stage.id, c]));
  const next = visibleStages.value.map(
    stage => existing.get(stage.id) || makeColumn(stage)
  );
  const added = next.filter(c => !existing.has(c.stage.id));
  columns.value = next;
  // Etapa activa en móvil: mantener la actual si sigue visible, si no la 1ª.
  if (!columns.value.some(c => c.stage.id === activeStageId.value)) {
    activeStageId.value = columns.value[0]?.stage.id ?? null;
  }
  return added;
};

// Arma las columnas (etapas + stats) y carga la 1ª página de cada una.
const buildColumns = async () => {
  const [{ data: stagesData }] = await Promise.all([
    PipelinesAPI.getStages(pipeline.value.id),
  ]);
  stages.value = orderStages(unwrap(stagesData));
  columns.value = [];
  syncColumns();
  await applyStats();
  await Promise.all(columns.value.map(column => loadColumnLeads(column, true)));
};

// Columna (etapa) activa en el layout móvil.
const activeColumn = computed(
  () =>
    columns.value.find(c => c.stage.id === activeStageId.value) ||
    columns.value[0] ||
    null
);

// Recarga stats + 1ª página de cada columna (al cambiar filtros / archivados),
// sin re-pedir las etapas. isReloading atenúa el tablero mientras tanto: sin
// esa señal, buscar/filtrar muestra resultados VIEJOS sin ningún aviso.
// Contador (no booleano): dos recargas solapadas no deben apagar la atenuación
// cuando termina la primera.
const pendingReloads = ref(0);
const isReloading = computed(() => pendingReloads.value > 0);
const reloadLeads = async () => {
  if (!pipeline.value) return;
  pendingReloads.value += 1;
  try {
    // Los filtros/búsqueda pueden revelar u ocultar columnas cerradas →
    // reconciliar ANTES de recargar (las nuevas se cargan con el resto).
    syncColumns();
    await applyStats();
    await Promise.all(
      columns.value.map(column => loadColumnLeads(column, true))
    );
  } finally {
    pendingReloads.value -= 1;
  }
};

// Menú de orden de una columna (uno abierto a la vez; clickaway en el wrapper).
const sortMenuStageId = ref(null);
const toggleSortMenu = columnId => {
  sortMenuStageId.value = sortMenuStageId.value === columnId ? null : columnId;
};
const closeSortMenu = columnId => {
  if (sortMenuStageId.value === columnId) sortMenuStageId.value = null;
};
const sortMenuSections = column => [
  {
    title: t('LEADS.SORT.TITLE'),
    items: [
      // Columnas cerradas: la opción natural es por fecha de cierre.
      ...(isClosedType(column.stage.stage_type)
        ? [
            {
              label: t('LEADS.SORT.CLOSED_RECENT'),
              action: 'sort',
              value: 'closed_recent',
              isSelected: sortForColumn(column) === 'closed_recent',
            },
          ]
        : []),
      {
        label: t('LEADS.SORT.MANUAL'),
        action: 'sort',
        value: 'manual',
        isSelected: sortForColumn(column) === 'manual',
      },
      {
        label: t('LEADS.SORT.RECENT'),
        action: 'sort',
        value: 'recent',
        isSelected: sortForColumn(column) === 'recent',
      },
      {
        label: t('LEADS.SORT.OLDEST'),
        action: 'sort',
        value: 'oldest',
        isSelected: sortForColumn(column) === 'oldest',
      },
    ],
  },
];
const setColumnSort = async (column, mode) => {
  await updateUISettings({
    leads_stage_sort: { ...stageSortSettings.value, [column.stage.id]: mode },
  });
  loadColumnLeads(column, true);
};
const onSortAction = (column, { value }) => {
  sortMenuStageId.value = null;
  if (value !== sortForColumn(column)) setColumnSort(column, value);
};

// "Resumen": UN solo Button + DropdownMenu para las estadísticas — mostrar/ocultar y
// su periodo. El periodo vive aquí adentro para dejar claro que aplica SOLO al resumen,
// no al tablero de leads.
const showResumenMenu = ref(false);
const resumenMenuSections = computed(() => [
  {
    title: t('LEADS.WIDGETS.PERIOD_TITLE'),
    items: PERIOD_OPTIONS.map(p => ({
      label: t(`LEADS.WIDGETS.PERIOD.${p.toUpperCase()}`),
      action: 'period',
      value: p,
      isSelected: period.value === p,
    })),
  },
  {
    items: [
      {
        label: t(
          showWidgets.value ? 'LEADS.WIDGETS.HIDE' : 'LEADS.WIDGETS.SHOW'
        ),
        action: 'toggle',
        value: 'toggle',
        icon: showWidgets.value ? 'i-lucide-eye-off' : 'i-lucide-eye',
      },
    ],
  },
]);
const onResumenAction = ({ action, value }) => {
  if (action === 'toggle') {
    toggleWidgets();
  } else if (action === 'period') {
    period.value = value;
    if (!showWidgets.value) updateUISettings({ show_pipeline_widgets: true });
  }
  showResumenMenu.value = false;
};

// Widget de tareas → Agenda con preset; widget de leads → filtra el tablero
// (el watcher de filtros recarga). Reseteamos los filtros de navegación previos.
const onNavigateAgenda = ({ preset }) => {
  router.push({ name: 'tasks_agenda', query: { preset } });
};

const onNavigateBoard = filters => {
  createdFrom.value = '';
  createdTo.value = '';
  createdPeriod.value = '';
  wonFrom.value = '';
  wonTo.value = '';
  wonPeriod.value = '';
  hasPendingTask.value = '';
  statusFilter.value = filters.status ?? '';
  if (filters.has_pending_task !== undefined) {
    hasPendingTask.value = filters.has_pending_task;
  }
  if (filters.period) {
    const range = periodDateRange(filters.period);
    createdFrom.value = range.from;
    createdTo.value = range.to;
    createdPeriod.value = filters.period;
  }
  // Card "Ganado": filtra por fecha de CIERRE (won_at), no de creación — la
  // card cuenta por won_at y la columna revelada debe cuadrar con su número.
  if (filters.won_period) {
    const range = periodDateRange(filters.won_period);
    wonFrom.value = range.from;
    wonTo.value = range.to;
    wonPeriod.value = filters.won_period;
  }
};

const loadBoard = async () => {
  isLoading.value = true;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = unwrap(data);
    pipeline.value = pickPipeline();
    if (!pipeline.value) {
      columns.value = [];
      return;
    }
    await buildColumns();
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Mover un lead a otra etapa (móvil, acción explícita). Optimista + persiste.
const openMoveDialog = lead => {
  leadToMove.value = lead;
  moveDialog.value.open();
};

// Picker de mover: TODAS las etapas (no solo columnas visibles), agrupadas
// Embudo / Cierre — sin esto, en móvil no habría forma de cerrar un lead.
const movePickerGroups = computed(() => groupedStages(stages.value));

// Movimiento local optimista (sin API): saca la card del origen, la mete al
// destino si está renderizado, y devuelve la función de REVERT exacta.
// Compartido por moveLeadTo y el flujo de cierre (cancelar el modal revierte).
const applyLocalMove = (lead, stageId) => {
  const fromStageId = lead.stage_id;
  const src = columns.value.find(c => c.stage.id === fromStageId);
  const tgt = columns.value.find(c => c.stage.id === stageId);
  const value = Number(lead.value) || 0;
  const srcIndex = src ? src.leads.findIndex(l => l.id === lead.id) : -1;
  if (src) {
    src.leads = src.leads.filter(l => l.id !== lead.id);
    src.count = Math.max(0, src.count - 1);
    src.valueSum -= value;
  }
  if (tgt) {
    tgt.leads.unshift(lead);
    tgt.count += 1;
    tgt.valueSum += value;
  }
  lead.stage_id = stageId;
  return () => {
    if (tgt) {
      tgt.leads = tgt.leads.filter(l => l.id !== lead.id);
      tgt.count = Math.max(0, tgt.count - 1);
      tgt.valueSum -= value;
    }
    if (src) {
      src.leads.splice(
        Math.min(Math.max(srcIndex, 0), src.leads.length),
        0,
        lead
      );
      src.count += 1;
      src.valueSum += value;
    }
    lead.stage_id = fromStageId;
    if (!src || !tgt) loadBoard();
  };
};

// Core de mover un lead a otra etapa SIN drag (picker móvil + "Aceptar" del
// triage de entrada). Optimista + revert local si falla.
const moveLeadTo = async (lead, stageId) => {
  if (!lead || stageId === lead.stage_id) return;
  const revert = applyLocalMove(lead, stageId);
  const tgt = columns.value.find(c => c.stage.id === stageId);
  try {
    await LeadsAPI.move(lead.id, stageId, 10);
    if (tgt) resortColumn(tgt);
    // Destino sin columna (etapa cerrada oculta): refrescar stats para que el
    // chip de descubrimiento refleje el lead que acaba de entrar ahí.
    if (!tgt) applyStats();
  } catch (error) {
    // Revert local: devolver la card a su columna/índice de origen (no
    // recargar todo el tablero por un error de red puntual).
    useAlert(t('LEADS.MOVE_ERROR'));
    revert();
  }
};

// --- Flujo de cierre (modal): toda transición a una etapa cerrada pasa por
// CloseLeadDialog. La operación pendiente lleva su PROPIO revert (nunca el
// lastRemoval compartido del drag: otro drag lo pisaría) y mientras el modal
// está abierto los drags de cards quedan bloqueados. ---
const closeDialog = ref(null);
const pendingClose = ref(null); // { lead, stage, revert, onSuccess }

const openCloseDialog = (lead, stage, { revert, onSuccess }) => {
  pendingClose.value = { lead, stage, revert, onSuccess };
  closeDialog.value.open(lead, stage);
};

const onCloseConfirm = async ({ lead, stage, extras }) => {
  const pending = pendingClose.value;
  try {
    await LeadsAPI.move(lead.id, stage.id, 10, extras);
    if (extras.value !== undefined) lead.value = extras.value;
    if (extras.lost_reason) lead.lost_reason = extras.lost_reason;
    pending?.onSuccess?.();
    // Null ANTES de settle: el @close que dispara el cierre no debe revertir.
    pendingClose.value = null;
    closeDialog.value.settle(true);
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('LEADS.MOVE_ERROR')
    );
    closeDialog.value.settle(false);
  }
};

// Cancelar/Esc/click-afuera: revertir SOLO si sigue pendiente (tras un confirm
// exitoso ya es null y esto es no-op).
const onCloseCancel = () => {
  if (!pendingClose.value) return;
  pendingClose.value.revert?.();
  pendingClose.value = null;
};

const moveLeadToStage = stageId => {
  const lead = leadToMove.value;
  moveDialog.value.close();
  const stage = stages.value.find(s => s.id === stageId);
  if (
    lead &&
    stage &&
    isClosedType(stage.stage_type) &&
    stageId !== lead.stage_id
  ) {
    // El picker ya se cerró (top layer): abrir el modal de cierre encadenado.
    const revert = applyLocalMove(lead, stageId);
    openCloseDialog(lead, stage, {
      revert,
      onSuccess: () => {
        const tgt = columns.value.find(c => c.stage.id === stageId);
        if (tgt) resortColumn(tgt);
        else applyStats();
      },
    });
    return;
  }
  moveLeadTo(lead, stageId);
};

// "Más viejo esperando" del header de la bandeja (presión honesta y visible).
const trayOldest = column => {
  if (column.stage.stage_type !== 'incoming' || !column.count) return null;
  return waitingSince(column.oldestAt);
};
const trayOldestLabel = info => {
  if (!info) return '';
  if (info.unit === 'now') return t('LEADS.TRIAGE.WAITING_NOW');
  if (info.unit === 'hours')
    return t('LEADS.TRIAGE.WAITING_HOURS', { n: info.n });
  return t('LEADS.TRIAGE.WAITING_DAYS', { n: info.n });
};
const WAITING_TONES = WAITING_TONE_CLASSES;

// --- Triage de la bandeja de entrada (estilo Kommo: aceptar / archivar) ---
// "Aceptar" = mover a la primera etapa activa (tipo open) del pipeline.
// Deriva de `stages` (fuente de verdad), no de las columnas renderizadas.
const firstOpenStage = computed(
  () => stages.value.find(s => s.stage_type === 'open') || null
);
const isTriageColumn = column =>
  column.stage.stage_type === 'incoming' &&
  !archivedView.value &&
  !!firstOpenStage.value;

const acceptLead = lead => {
  if (firstOpenStage.value) moveLeadTo(lead, firstOpenStage.value.id);
};

// Archivar desde la card (con la misma confirmación que la ficha: para un
// agente es irreversible de facto — restaurar es solo-admin).
const boardArchiveDialog = ref(null);
const leadToArchive = ref(null);
const isBoardArchiving = ref(false);
const confirmArchiveLead = lead => {
  leadToArchive.value = lead;
  boardArchiveDialog.value.open();
};
const archiveLeadFromBoard = async () => {
  const lead = leadToArchive.value;
  if (!lead || isBoardArchiving.value) return;
  isBoardArchiving.value = true;
  try {
    await LeadsAPI.delete(lead.id);
    const column = columns.value.find(c => c.stage.id === lead.stage_id);
    if (column) {
      column.leads = column.leads.filter(l => l.id !== lead.id);
      column.count = Math.max(0, column.count - 1);
      column.valueSum -= Number(lead.value) || 0;
    }
    boardArchiveDialog.value?.close();
    useAlert(t('LEADS.ARCHIVE.ARCHIVED_SUCCESS'));
  } catch (error) {
    useAlert(t('LEADS.FORM.ERROR'));
  } finally {
    isBoardArchiving.value = false;
    leadToArchive.value = null;
  }
};

// ---- Selector y gestión de pipelines (multi-pipeline) ----
const switchPipeline = async id => {
  if (pipeline.value?.id === id) return;
  const next = pipelines.value.find(p => p.id === id);
  if (!next) return;
  pipeline.value = next;
  updateUISettings({ last_pipeline_id: id });
  isLoading.value = true;
  try {
    await buildColumns();
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openPipelineDialog = mode => {
  pipelineDialogMode.value = mode;
  pipelineNameInput.value = mode === 'rename' ? pipeline.value?.name || '' : '';
  pipelineDialog.value.open();
};

const submitPipelineDialog = async () => {
  const name = pipelineNameInput.value.trim();
  if (!name) return;
  try {
    if (pipelineDialogMode.value === 'create') {
      const { data } = await PipelinesAPI.create({ pipeline: { name } });
      const created = unwrap(data);
      await updateUISettings({ last_pipeline_id: created.id });
      pipelineDialog.value.close();
      await loadBoard();
    } else {
      await PipelinesAPI.update(pipeline.value.id, { pipeline: { name } });
      pipeline.value.name = name;
      const inList = pipelines.value.find(p => p.id === pipeline.value.id);
      if (inList) inList.name = name;
      pipelineDialog.value.close();
    }
  } catch (error) {
    useAlert(t('LEADS.PIPELINE.ERROR'));
  }
};

const setDefaultPipeline = async () => {
  try {
    await PipelinesAPI.update(pipeline.value.id, {
      pipeline: { is_default: true },
    });
    // Refresca los flags is_default sin perder las columnas cargadas.
    const { data } = await PipelinesAPI.get();
    pipelines.value = unwrap(data);
    pipeline.value =
      pipelines.value.find(p => p.id === pipeline.value.id) || pipeline.value;
    useAlert(t('LEADS.PIPELINE.DEFAULT_SUCCESS'));
  } catch (error) {
    useAlert(t('LEADS.PIPELINE.ERROR'));
  }
};

const handleDeletePipeline = async () => {
  try {
    await PipelinesAPI.delete(pipeline.value.id);
    deletePipelineDialog.value.close();
    await updateUISettings({ last_pipeline_id: null });
    await loadBoard();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('LEADS.PIPELINE.ERROR'));
  }
};

// Alterna el tablero entre activos y archivados (solo admin). Recarga columnas.
const toggleArchivedView = async () => {
  archivedView.value = !archivedView.value;
  if (!pipeline.value) return;
  isLoading.value = true;
  try {
    await reloadLeads();
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Exporta los leads del pipeline actual (respeta los filtros activos) a CSV.
const exportLeads = async () => {
  isExporting.value = true;
  try {
    const { data } = await LeadsAPI.exportLeads(leadQueryParams.value);
    const slug = (pipeline.value?.name || 'leads')
      .toLowerCase()
      .replace(/\s+/g, '-');
    downloadBlob(data, `leads-${slug}.csv`);
  } catch (error) {
    useAlert(t('LEADS.EXPORT.ERROR'));
  } finally {
    isExporting.value = false;
  }
};

const handlePipelineMenuAction = ({ action, value }) => {
  showPipelineMenu.value = false;
  if (action === 'switch') switchPipeline(value);
  else if (action === 'create') openPipelineDialog('create');
  else if (action === 'rename') openPipelineDialog('rename');
  else if (action === 'set-default') setDefaultPipeline();
  else if (action === 'delete') deletePipelineDialog.value.open();
  else if (action === 'toggle-archived') toggleArchivedView();
  else if (action === 'export') exportLeads();
  else if (action === 'toggle-closed' && hasClosedStages.value)
    toggleShowClosed();
  else if (action === 'loss-reasons') lossReasonsDialog.value.open();
};

const clearFilters = () => {
  search.value = '';
  assigneeFilter.value = '';
  statusFilter.value = '';
  createdFrom.value = '';
  createdTo.value = '';
  createdPeriod.value = '';
  wonFrom.value = '';
  wonTo.value = '';
  wonPeriod.value = '';
  hasPendingTask.value = '';
};

// Espejo filtros → URL (router.replace = no apila historial). Solo keys con
// valor: URL limpia cuando no hay filtros.
const filtersToQuery = () => {
  const query = {};
  if (search.value) query.q = search.value;
  if (assigneeFilter.value) query.assignee = String(assigneeFilter.value);
  if (statusFilter.value) query.status = statusFilter.value;
  if (createdFrom.value) query.created_from = createdFrom.value;
  if (createdTo.value) query.created_to = createdTo.value;
  if (createdPeriod.value) query.created_period = createdPeriod.value;
  if (wonFrom.value) query.won_from = wonFrom.value;
  if (wonTo.value) query.won_to = wonTo.value;
  if (wonPeriod.value) query.won_period = wonPeriod.value;
  if (hasPendingTask.value) query.pending_task = hasPendingTask.value;
  return query;
};
watch(
  [
    search,
    assigneeFilter,
    statusFilter,
    createdFrom,
    createdTo,
    createdPeriod,
    wonFrom,
    wonTo,
    wonPeriod,
    hasPendingTask,
  ],
  () => router.replace({ query: filtersToQuery() })
);

// Búsqueda con debounce; agente/estado aplican al instante (recargan stats + pág.1).
let searchTimer = null;
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(reloadLeads, 300);
});
watch(
  [
    assigneeFilter,
    statusFilter,
    createdFrom,
    createdTo,
    wonFrom,
    wonTo,
    hasPendingTask,
  ],
  reloadLeads
);
// El toggle "Mostrar cerradas" revela/oculta columnas → reconciliar + cargar.
watch(showClosed, reloadLeads);

// Tuning del drag (compartido por ambos Draggables). Se pasa como objeto por
// v-bind a propósito: `delayOnTouchOnly` debe llegar como booleano `true` para
// que el delay aplique SOLO en táctil (long-press para arrastrar vs. swipe para
// scroll) y el mouse en desktop arrastre inmediato. Como atributo "shorthand"
// (delay-on-touch-only) vuedraggable lo recibe como "" (falsy) vía $attrs y el
// delay terminaría aplicando también al mouse (drag lento en desktop).
const dragTuning = {
  delay: 180,
  delayOnTouchOnly: true,
  touchStartThreshold: 8,
};

// ---- Lead drag entre etapas (ajusta conteos/sumas server-side localmente) ----

// Último 'removed' del drag en curso: origen + índice, para poder devolver la
// card a su lugar exacto si el move falla (en vez de recargar TODO el tablero,
// que pierde scroll y páginas de "Cargar más").
let lastRemoval = null;

const revertLeadMove = (event, column, lead, fromStageId) => {
  if (event.moved) {
    // Reorden dentro de la misma columna: volver la card a su índice original.
    const list = column.leads;
    const idx = list.findIndex(l => l.id === lead.id);
    if (idx !== -1) {
      list.splice(idx, 1);
      list.splice(Math.min(event.moved.oldIndex, list.length), 0, lead);
    }
    return;
  }
  // Cross-columna: sacar del destino y devolver al origen (índice original).
  const value = Number(lead.value) || 0;
  const tgtIdx = column.leads.findIndex(l => l.id === lead.id);
  if (tgtIdx !== -1) column.leads.splice(tgtIdx, 1);
  column.count = Math.max(0, column.count - 1);
  column.valueSum -= value;
  const src = columns.value.find(c => c.stage.id === fromStageId);
  if (!src) {
    // La columna origen ya no existe (borrada en paralelo): último recurso.
    loadBoard();
    return;
  }
  const at =
    lastRemoval &&
    lastRemoval.leadId === lead.id &&
    lastRemoval.columnId === src.id
      ? Math.min(lastRemoval.index, src.leads.length)
      : 0;
  src.leads.splice(at, 0, lead);
  src.count += 1;
  src.valueSum += value;
  lead.stage_id = fromStageId;
};

const onLeadChange = async (event, column) => {
  // vuedraggable dispara 'removed' en la columna origen y 'added' en la destino.
  if (event.removed) {
    column.count = Math.max(0, column.count - 1);
    column.valueSum -= Number(event.removed.element.value) || 0;
    lastRemoval = {
      columnId: column.id,
      index: event.removed.oldIndex,
      leadId: event.removed.element.id,
    };
    return; // el move lo dispara el 'added' del destino
  }
  const moved = event.added || event.moved;
  if (!moved) return;

  const lead = moved.element;
  const position = (moved.newIndex + 1) * 10;
  const fromStageId = lead.stage_id; // antes de pisarlo, para poder revertir
  if (event.added) {
    column.count += 1;
    column.valueSum += Number(lead.value) || 0;
    lead.stage_id = column.stage.id;
  }
  // Drag HACIA una columna cerrada (revelada): el cierre pasa por el modal —
  // nada de API acá; confirmar persiste, cancelar revierte este mismo drag.
  if (event.added && isClosedType(column.stage.stage_type)) {
    openCloseDialog(lead, column.stage, {
      revert: () => revertLeadMove(event, column, lead, fromStageId),
      onSuccess: () => resortColumn(column),
    });
    return;
  }
  // Arrastrar DENTRO de una columna ordenada por fecha = intención explícita
  // de orden manual: el modo cambia (persistido) y el drop se respeta.
  if (event.moved && sortForColumn(column) !== 'manual') {
    updateUISettings({
      leads_stage_sort: {
        ...stageSortSettings.value,
        [column.stage.id]: 'manual',
      },
    });
    useAlert(t('LEADS.SORT.MANUAL_ENABLED'));
  }
  try {
    await LeadsAPI.move(lead.id, column.stage.id, position);
    // Al caer en una columna ordenada por fecha, la card se acomoda donde le
    // corresponde (el drop-index solo tiene sentido en orden manual).
    if (event.added) resortColumn(column);
  } catch (error) {
    useAlert(t('LEADS.MOVE_ERROR'));
    revertLeadMove(event, column, lead, fromStageId);
  }
};

// --- Zonas de drop de cierre (aparecen al arrastrar; patrón Kommo) ---
const isDraggingLead = ref(false);
const wonStage = computed(
  () => stages.value.find(s => s.stage_type === 'won') || null
);
const lostStage = computed(
  () => stages.value.find(s => s.stage_type === 'lost') || null
);

// Card soltada en una zona: la columna origen ya disparó su 'removed' (conteos
// + lastRemoval). Snapshot PROPIO del origen para el revert del modal.
const onZoneDrop = ({ lead, stage }) => {
  const removal =
    lastRemoval && lastRemoval.leadId === lead.id ? { ...lastRemoval } : null;
  const value = Number(lead.value) || 0;
  const restoreToOrigin = () => {
    const src = columns.value.find(c => c.id === removal?.columnId);
    if (!src) {
      loadBoard();
      return;
    }
    src.leads.splice(Math.min(removal.index, src.leads.length), 0, lead);
    src.count += 1;
    src.valueSum += value;
  };
  // Soltó la card de la misma etapa en su zona: no-op, devolverla.
  if (lead.stage_id === stage.id) {
    restoreToOrigin();
    return;
  }
  openCloseDialog(lead, stage, {
    revert: restoreToOrigin,
    onSuccess: () => {
      lead.stage_id = stage.id;
      const tgt = columns.value.find(c => c.stage.id === stage.id);
      if (tgt) {
        tgt.leads.unshift(lead);
        tgt.count += 1;
        tgt.valueSum += value;
        resortColumn(tgt);
      } else {
        applyStats();
      }
    },
  });
};

// ---- Lead create / edit (ambos = página LeadDetail) ----
const openCreateLead = () =>
  router.push({ name: 'leads_new', params: { accountId: accountId.value } });
const openEditLead = lead =>
  router.push({
    name: 'leads_show',
    params: { accountId: accountId.value, leadId: lead.id },
  });

const openConversation = lead => {
  if (!lead.conversation) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: lead.conversation.display_id,
    },
  });
};

// ---- Stage management (admin) ----
const startRenameStage = stage => {
  if (!isAdmin.value) return;
  editingStageId.value = stage.id;
  editingStageName.value = stage.name;
};

const saveStageName = async stage => {
  const name = editingStageName.value.trim();
  editingStageId.value = null;
  if (!name || name === stage.name) return;
  try {
    await PipelinesAPI.updateStage(pipeline.value.id, stage.id, {
      pipeline_stage: { name },
    });
    stage.name = name;
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
  }
};

const addStage = async () => {
  try {
    const { data } = await PipelinesAPI.createStage(pipeline.value.id, {
      pipeline_stage: { name: t('LEADS.STAGE.NEW_NAME') },
    });
    const stage = unwrap(data);
    columns.value.push(makeColumn(stage));
    startRenameStage(stage);
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
  }
};

const confirmDeleteStage = stage => {
  stageToDelete.value = stage;
  deleteStageDialog.value.open();
};

// Decir CUÁNTOS leads se van a mover, no confirmar a ciegas. El conteo de la
// columna respeta los filtros activos (podría no ser el total real) → con
// filtros o en vista archivados se usa el texto genérico.
const deleteStageDescription = computed(() => {
  if (hasActiveFilters.value || archivedView.value) {
    return t('LEADS.STAGE.DELETE_CONFIRM');
  }
  const count =
    columns.value.find(c => c.stage.id === stageToDelete.value?.id)?.count ?? 0;
  return count > 0
    ? t('LEADS.STAGE.DELETE_CONFIRM_COUNT', { count })
    : t('LEADS.STAGE.DELETE_CONFIRM_EMPTY');
});

const handleDeleteStage = async () => {
  if (!stageToDelete.value) return;
  try {
    await PipelinesAPI.deleteStage(pipeline.value.id, stageToDelete.value.id);
    deleteStageDialog.value.close();
    stageToDelete.value = null;
    await loadBoard();
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
  }
};

// ---- Tipo de etapa + motivos de pérdida (handlers; refs arriba, junto a los
// demás dialogs — el lint exige definición antes de uso también en closures) ----
const onStageTypeUpdated = async () => {
  // El retipado mueve columnas entre embudo/cierre y resincroniza leads:
  // rebuild completo (fábrica + stats + widgets vía re-render).
  await loadBoard();
};
const onLossReasonsUpdated = reasons => {
  if (pipeline.value) pipeline.value.loss_reasons = reasons;
  const inList = pipelines.value.find(pl => pl.id === pipeline.value?.id);
  if (inList) inList.loss_reasons = reasons;
};

const onColumnReorder = async event => {
  // @change en el Draggable externo solo dispara al reordenar columnas; el
  // guard evita actuar si el evento no es un movimiento de columna.
  if (event && !event.moved) return;

  const positionsHash = {};
  columns.value.forEach((column, index) => {
    const position = (index + 1) * 10;
    positionsHash[column.stage.id] = position;
    column.stage.position = position;
  });
  try {
    await PipelinesAPI.reorderStages(pipeline.value.id, positionsHash);
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
    loadBoard();
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  loadBoard();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <PageHeader
      v-model:search="search"
      :search-placeholder="pipeline ? t('LEADS.FILTER.SEARCH_PLACEHOLDER') : ''"
    >
      <template #title>
        <h1 class="flex-shrink-0 text-xl font-semibold text-n-slate-12">
          {{ t('LEADS.TITLE') }}
        </h1>
        <div
          v-if="pipeline"
          v-on-clickaway="() => (showPipelineMenu = false)"
          class="relative flex items-center min-w-0 gap-1.5"
        >
          <span class="flex-shrink-0 text-n-slate-9">/</span>
          <!-- Pipeline selector trigger: kept as <button> (not Button component) because
               it lives inline in the PageHeader #title slot alongside a <h1> and needs
               min-w-0 truncation on its text child — Button's own layout classes would
               override this. Tokens are n-* throughout. -->
          <button
            class="flex items-center min-w-0 gap-1 text-sm rounded-md text-n-slate-11 hover:text-n-slate-12"
            @click="showPipelineMenu = !showPipelineMenu"
          >
            <span class="truncate">{{ pipeline.name }}</span>
            <span class="flex-shrink-0 i-lucide-chevron-down size-3.5" />
          </button>
          <span
            v-if="archivedView"
            class="inline-flex flex-shrink-0 items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
          >
            <span class="i-lucide-archive size-3" />
            {{ t('LEADS.PIPELINE.ARCHIVED_BADGE') }}
          </span>
          <!-- Agente filtrado: en la FILA DEL TÍTULO (identidad de la vista:
               "Ventas · de Belén"), mismo patrón que el badge de Archivados. -->
          <button
            v-if="filteredAgent"
            type="button"
            class="inline-flex flex-shrink-0 items-center gap-1.5 py-0.5 pl-0.5 pr-2 text-xs font-semibold rounded-full bg-n-brand text-n-solid-1 hover:opacity-90"
            :title="t('LEADS.FILTER.REMOVE')"
            @click.stop="removeFilterChip('assignee')"
          >
            <Avatar
              :name="filteredAgent.name || '?'"
              :src="filteredAgent.thumbnail"
              :size="20"
              rounded-full
            />
            <span class="max-w-[8rem] truncate">{{ filteredAgent.name }}</span>
            <span class="i-lucide-x size-3" aria-hidden="true" />
          </button>
          <DropdownMenu
            v-if="showPipelineMenu"
            :menu-sections="pipelineMenuSections"
            class="left-0 top-8"
            @action="handlePipelineMenuAction"
          />
        </div>
      </template>

      <!-- Orden: (búsqueda, la pone PageHeader) · Filtros · Resumen · Periodo · Nuevo.
           Todos Button md (40px) → alturas parejas sin overrides. -->
      <template v-if="pipeline" #actions>
        <div class="relative">
          <Button
            slate
            ghost
            icon="i-lucide-sliders-horizontal"
            :title="t('LEADS.FILTER.TITLE')"
            @click="filtersDialog.open()"
          />
          <span
            v-if="hasNonSearchFilters"
            class="absolute rounded-full pointer-events-none top-1 right-1 size-1.5 bg-n-brand"
          />
        </div>
        <!-- Resumen: un solo Button + DropdownMenu (mostrar/ocultar + periodo de las
             estadísticas). El periodo NO toca el tablero de leads, solo el resumen. -->
        <div
          v-if="!archivedView"
          v-on-clickaway="() => (showResumenMenu = false)"
          class="relative"
        >
          <Button
            :color="showWidgets ? 'blue' : 'slate'"
            :variant="showWidgets ? 'faded' : 'ghost'"
            icon="i-lucide-layout-dashboard"
            :title="t('LEADS.WIDGETS.TOGGLE')"
            @click="showResumenMenu = !showResumenMenu"
          />
          <DropdownMenu
            v-if="showResumenMenu"
            :menu-sections="resumenMenuSections"
            class="right-0 top-11 min-w-[12rem]"
            @action="onResumenAction"
          />
        </div>
        <Button
          v-if="!archivedView"
          blue
          solid
          icon="i-lucide-plus"
          :title="t('LEADS.NEW_SHORT')"
          class="md:hidden"
          @click="openCreateLead"
        />
        <Button
          v-if="!archivedView"
          blue
          solid
          icon="i-lucide-plus"
          :label="t('LEADS.NEW_SHORT')"
          class="hidden md:flex"
          @click="openCreateLead"
        />
      </template>
    </PageHeader>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>

    <!-- Sin pipeline: no dejar al usuario en un callejón sin salida — el admin
         puede crear el primero acá mismo; al agente se le dice a quién pedir. -->
    <div
      v-else-if="!pipeline"
      class="flex flex-col items-center justify-center flex-1 gap-3 text-n-slate-11"
    >
      <span class="i-lucide-kanban size-8 text-n-slate-9" />
      <p>{{ t('LEADS.EMPTY') }}</p>
      <Button
        v-if="isAdmin"
        blue
        solid
        icon="i-lucide-plus"
        :label="t('LEADS.PIPELINE.NEW')"
        @click="openPipelineDialog('create')"
      />
      <p v-else class="text-sm text-n-slate-10">
        {{ t('LEADS.EMPTY_ASK_ADMIN') }}
      </p>
    </div>

    <template v-else>
      <!-- Chips de filtros activos: los filtros que llegan por widget no tienen
           otra señal visible; el tablero nunca debe filtrar en silencio. Pills
           a mano (patrón del badge "Archivado"): un Button xs es más alto que
           el pill y rompe la línea. -->
      <div
        v-if="activeFilterChips.length"
        class="flex flex-wrap items-center flex-shrink-0 gap-2 px-4 pt-3 sm:px-6"
      >
        <button
          v-for="chip in activeFilterChips"
          :key="chip.key"
          type="button"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12"
          :title="t('LEADS.FILTER.REMOVE')"
          @click="removeFilterChip(chip.key)"
        >
          {{ chip.label }}
          <span class="i-lucide-x size-3" aria-hidden="true" />
        </button>
        <Button
          v-if="(filteredAgent ? 1 : 0) + activeFilterChips.length > 1"
          link
          size="xs"
          :label="t('LEADS.FILTER.CLEAR')"
          @click="clearFilters"
        />
      </div>

      <!-- Filtro Ganados/Perdidos sin etapa de ese tipo en el pipeline: avisar
           (columnas del embudo vacías sin explicación parecen un bug). -->
      <div
        v-if="missingClosedStageType"
        class="flex items-center flex-shrink-0 gap-2 px-4 pt-3 sm:px-6"
      >
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-n-amber-3 text-n-amber-12"
        >
          <span class="i-lucide-info size-3.5" aria-hidden="true" />
          {{
            t(
              missingClosedStageType === 'won'
                ? 'LEADS.CLOSED.NO_WON_STAGE'
                : 'LEADS.CLOSED.NO_LOST_STAGE'
            )
          }}
        </span>
      </div>

      <!-- Panel de widgets (el toggle "Resumen" vive en el header principal) -->
      <div v-if="widgetsVisible" class="flex-shrink-0 px-4 pt-3 sm:px-6">
        <PipelineWidgets
          :pipeline-id="pipeline.id"
          :currency="pipeline.currency"
          :assignee-id="assigneeFilter"
          :period="period"
          :status-filter="statusFilter"
          :has-pending-task="hasPendingTask"
          :created-period="createdPeriod"
          @navigate-agenda="onNavigateAgenda"
          @navigate-board="onNavigateBoard"
        />
      </div>

      <!-- Desktop/tablet: Kanban horizontal con drag (long-press en táctil).
           Mientras se recarga por búsqueda/filtro, el tablero se atenúa: sin
           esta señal se muestran resultados viejos como si fueran actuales. -->
      <div
        class="relative flex-1 hidden p-4 overflow-x-auto overflow-y-hidden transition-opacity md:block"
        :class="{ 'opacity-60 pointer-events-none': isReloading }"
        :aria-busy="isReloading"
      >
        <div
          class="flex items-start h-full gap-4 min-w-max"
          :class="{ 'pb-24': isDraggingLead }"
        >
          <Draggable
            :list="columns"
            item-key="id"
            handle=".stage-grip"
            :group="{ name: 'stages' }"
            :disabled="!isAdmin"
            v-bind="dragTuning"
            class="flex items-start h-full gap-4"
            @change="onColumnReorder"
          >
            <template #item="{ element: column }">
              <!-- La etapa de entrada se ve como BANDEJA (estilo Kommo: zona de
                   triage distinta, no una etapa más): fondo propio + icono. -->
              <!-- Cerradas (won/lost): header con icono de tipo + tinte muted —
                   la señal de QUÉ es la columna viaja siempre con ella. -->
              <div
                class="relative flex flex-col flex-shrink-0 rounded-xl w-72 max-h-full"
                :class="
                  column.stage.stage_type === 'incoming'
                    ? 'bg-n-amber-2 border-2 border-dashed border-n-amber-7'
                    : 'bg-n-alpha-1'
                "
              >
                <!-- Header de la BANDEJA: dos filas — el nombre respira en la
                     primera; conteo + reloj viven en la segunda. Sin suma de
                     plata acá: el triage es de volumen y espera, no de valor. -->
                <div
                  v-if="column.stage.stage_type === 'incoming'"
                  class="flex flex-col gap-1 px-3 py-2.5 border-b border-n-amber-5"
                >
                  <div class="flex items-center gap-1.5">
                    <span
                      v-if="isAdmin"
                      class="flex-shrink-0 cursor-grab stage-grip i-lucide-grip-vertical size-3.5 text-n-amber-9"
                    />
                    <span
                      class="flex-shrink-0 size-3.5 text-n-amber-11"
                      :class="stageTypeIcon('incoming')"
                    />
                    <Input
                      v-if="editingStageId === column.stage.id"
                      v-model="editingStageName"
                      autofocus
                      custom-input-class="!py-0.5 !text-sm"
                      @keyup.enter="saveStageName(column.stage)"
                      @blur="saveStageName(column.stage)"
                    />
                    <span
                      v-else
                      class="flex-1 min-w-0 text-sm font-semibold truncate text-n-amber-12"
                      :class="{ 'cursor-text': isAdmin }"
                      @click="startRenameStage(column.stage)"
                    >
                      {{ column.stage.name }}
                    </span>
                    <div
                      v-on-clickaway="() => closeSortMenu(column.id)"
                      class="relative flex-shrink-0"
                    >
                      <Button
                        ghost
                        icon="i-lucide-arrow-up-down"
                        size="xs"
                        :title="t('LEADS.SORT.TITLE')"
                        :class="
                          sortForColumn(column) !== 'manual'
                            ? 'text-n-brand'
                            : 'text-n-amber-10'
                        "
                        @click.stop="toggleSortMenu(column.id)"
                      />
                      <DropdownMenu
                        v-if="sortMenuStageId === column.id"
                        :menu-sections="sortMenuSections(column)"
                        class="right-0 top-7 z-20 min-w-[13rem]"
                        @action="onSortAction(column, $event)"
                      />
                    </div>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <span
                      class="px-1.5 py-0.5 text-xs font-semibold rounded-full"
                      :class="
                        column.count > 0
                          ? 'bg-n-amber-4 text-n-amber-12'
                          : 'bg-n-amber-3 text-n-amber-10'
                      "
                    >
                      {{ column.count }}
                    </span>
                    <span
                      v-if="trayOldest(column)"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-full"
                      :class="WAITING_TONES[trayOldest(column).tone]"
                      :title="t('LEADS.TRIAGE.OLDEST_TITLE')"
                    >
                      <span class="i-lucide-clock size-3" aria-hidden="true" />
                      {{ trayOldestLabel(trayOldest(column)) }}
                    </span>
                  </div>
                </div>

                <div
                  v-else
                  class="flex items-center justify-between gap-1 px-3 py-2.5 border-b border-n-weak"
                  :class="{
                    'opacity-80': isClosedType(column.stage.stage_type),
                  }"
                >
                  <div class="flex items-center min-w-0 gap-1.5">
                    <span
                      v-if="isAdmin"
                      class="flex-shrink-0 cursor-grab stage-grip i-lucide-grip-vertical size-3.5 text-n-slate-9"
                    />
                    <span
                      v-if="isClosedType(column.stage.stage_type)"
                      class="flex-shrink-0 size-3.5 text-n-slate-10"
                      :class="stageTypeIcon(column.stage.stage_type)"
                    />
                    <span
                      v-else
                      class="flex-shrink-0 rounded-full size-2.5"
                      :style="{
                        backgroundColor: column.stage.color || '#94A3B8',
                      }"
                    />
                    <Input
                      v-if="editingStageId === column.stage.id"
                      v-model="editingStageName"
                      autofocus
                      custom-input-class="!py-0.5 !text-sm"
                      @keyup.enter="saveStageName(column.stage)"
                      @blur="saveStageName(column.stage)"
                    />
                    <span
                      v-else
                      class="text-sm font-medium truncate text-n-slate-12"
                      :class="{ 'cursor-text': isAdmin }"
                      @click="startRenameStage(column.stage)"
                    >
                      {{ column.stage.name }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ column.count }}
                    </span>
                  </div>
                  <div class="flex items-center flex-shrink-0 gap-1">
                    <span
                      v-if="column.valueSum > 0"
                      class="text-xs font-semibold text-n-teal-11"
                    >
                      {{
                        formatLeadCurrency(column.valueSum, pipeline?.currency)
                      }}
                    </span>
                    <!-- Orden de la columna (per-usuario). Clickaway en el
                         WRAPPER para que re-clickear el botón cierre. -->
                    <div
                      v-on-clickaway="() => closeSortMenu(column.id)"
                      class="relative"
                    >
                      <Button
                        ghost
                        icon="i-lucide-arrow-up-down"
                        size="xs"
                        :title="t('LEADS.SORT.TITLE')"
                        :class="
                          sortForColumn(column) !== 'manual'
                            ? 'text-n-brand'
                            : 'text-n-slate-10'
                        "
                        @click.stop="toggleSortMenu(column.id)"
                      />
                      <DropdownMenu
                        v-if="sortMenuStageId === column.id"
                        :menu-sections="sortMenuSections(column)"
                        class="right-0 top-7 z-20 min-w-[13rem]"
                        @action="onSortAction(column, $event)"
                      />
                    </div>
                    <Button
                      v-if="isAdmin && column.stage.stage_type !== 'incoming'"
                      ghost
                      icon="i-lucide-shapes"
                      size="xs"
                      class="text-n-slate-10"
                      :title="t('LEADS.STAGE_TYPE_DIALOG.MENU')"
                      @click.stop="openStageTypeDialog(column.stage)"
                    />
                    <Button
                      v-if="isAdmin && column.stage.deletable"
                      ghost
                      icon="i-lucide-trash-2"
                      size="xs"
                      class="text-n-slate-10 hover:text-n-ruby-11"
                      @click.stop="confirmDeleteStage(column.stage)"
                    />
                  </div>
                </div>

                <p
                  v-if="isTriageColumn(column) && column.count > 0"
                  class="flex-shrink-0 px-3 pt-1.5 text-[11px] text-n-amber-11"
                >
                  {{ t('LEADS.TRIAGE.HINT') }}
                </p>
                <Draggable
                  :list="column.leads"
                  item-key="id"
                  :group="{ name: 'leads' }"
                  :disabled="archivedView || !!pendingClose"
                  v-bind="dragTuning"
                  class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-[60px]"
                  ghost-class="opacity-40"
                  @start="isDraggingLead = true"
                  @end="isDraggingLead = false"
                  @change="onLeadChange($event, column)"
                >
                  <template #item="{ element }">
                    <LeadCard
                      :lead="element"
                      :triage="isTriageColumn(column)"
                      @open="openConversation"
                      @edit="openEditLead"
                      @accept="acceptLead"
                      @archive="confirmArchiveLead"
                    />
                  </template>
                  <!-- Vacío: dentro del footer del Draggable → sigue siendo
                       zona droppable y no deja un hueco mudo que parece error.
                       Con filtro de estado activo el texto lo dice (los leads
                       existen, están filtrados); cerradas con su propio copy. -->
                  <template #footer>
                    <div
                      v-if="!column.leads.length && !column.loadingMore"
                      class="flex flex-col items-center gap-0.5 px-3 py-5 text-center"
                    >
                      <p class="text-xs text-n-slate-10">
                        {{ emptyColumnLabel(column) }}
                      </p>
                      <p
                        v-if="!archivedView && !statusFilter"
                        class="text-xs text-n-slate-9"
                      >
                        {{ t('LEADS.EMPTY_STAGE_HINT') }}
                      </p>
                    </div>
                  </template>
                </Draggable>

                <Button
                  v-if="column.hasMore"
                  ghost
                  color="slate"
                  size="sm"
                  class="flex-shrink-0 mx-2 mb-2"
                  :is-loading="column.loadingMore"
                  :label="
                    t('LEADS.LOAD_MORE', {
                      count: column.count - column.leads.length,
                    })
                  "
                  @click="loadColumnLeads(column, false)"
                />
              </div>
            </template>
          </Draggable>

          <!-- Kept as Button component for consistent ghost styling. -->
          <Button
            v-if="isAdmin && pipeline"
            ghost
            color="slate"
            icon="i-lucide-plus"
            :label="t('LEADS.STAGE.ADD')"
            class="flex-shrink-0 rounded-xl"
            @click="addStage"
          />

          <!-- Descubrimiento de las cerradas ocultas: affordance permanente
               (sin esto, los leads cerrados "desaparecen" sin explicación y el
               toggle queda enterrado en el menú). -->
          <button
            v-if="hiddenClosedCount > 0"
            type="button"
            class="inline-flex items-center flex-shrink-0 gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4 hover:text-n-slate-12"
            @click="toggleShowClosed"
          >
            <span class="i-lucide-trophy size-3" aria-hidden="true" />
            {{ t('LEADS.CLOSED.DISCOVERY', { count: hiddenClosedCount }) }}
          </button>
          <span
            v-else-if="closedRevealedBySearch"
            class="inline-flex items-center flex-shrink-0 gap-1.5 px-3 py-1.5 text-xs rounded-full bg-n-slate-2 text-n-slate-10"
          >
            {{ t('LEADS.CLOSED.REVEALED_BY_SEARCH') }}
          </span>
        </div>

        <!-- Zonas "✓ Ganado / ✗ Perdido" visibles solo durante el drag. -->
        <CloseDropZones
          :won-stage="wonStage"
          :lost-stage="lostStage"
          :active="isDraggingLead && !archivedView"
          @drop="onZoneDrop"
        />
      </div>

      <!-- Móvil: una etapa a la vez (chips de navegación + lista vertical + mover) -->
      <div
        class="flex flex-col flex-1 min-h-0 transition-opacity md:hidden"
        :class="{ 'opacity-60 pointer-events-none': isReloading }"
        :aria-busy="isReloading"
      >
        <div
          class="flex flex-shrink-0 gap-2 px-3 py-2 overflow-x-auto border-b border-n-weak"
        >
          <!-- Stage chip: kept as <button> (not Button component) because the active
               state needs a custom pill shape (rounded-full) + brand background that
               doesn't map cleanly to any Button variant. Layout is structural. -->
          <button
            v-for="column in columns"
            :key="column.id"
            class="flex items-center flex-shrink-0 gap-1.5 px-3 py-1.5 text-sm rounded-full"
            :class="
              column.stage.id === activeStageId
                ? 'bg-n-brand text-n-solid-1'
                : column.stage.stage_type === 'incoming'
                  ? 'bg-n-amber-3 text-n-amber-11 border border-dashed border-n-amber-7'
                  : 'bg-n-alpha-1 text-n-slate-11'
            "
            @click="activeStageId = column.stage.id"
          >
            <span
              v-if="
                column.stage.stage_type === 'incoming' ||
                isClosedType(column.stage.stage_type)
              "
              class="size-3"
              :class="stageTypeIcon(column.stage.stage_type)"
            />
            <span
              v-else
              class="rounded-full size-2"
              :style="{ backgroundColor: column.stage.color || '#94A3B8' }"
            />
            {{ column.stage.name }}
            <span class="text-xs opacity-80">{{ column.count }}</span>
          </button>
          <!-- Descubrimiento de cerradas ocultas (paridad con desktop). -->
          <button
            v-if="hiddenClosedCount > 0"
            type="button"
            class="flex items-center flex-shrink-0 gap-1.5 px-3 py-1.5 text-xs rounded-full bg-n-slate-3 text-n-slate-11"
            @click="toggleShowClosed"
          >
            <span class="i-lucide-trophy size-3" aria-hidden="true" />
            {{ t('LEADS.CLOSED.DISCOVERY', { count: hiddenClosedCount }) }}
          </button>
        </div>

        <div
          v-if="activeColumn"
          class="flex flex-col flex-1 gap-2 p-3 overflow-y-auto"
        >
          <div class="flex items-center justify-between gap-2">
            <span
              v-if="trayOldest(activeColumn)"
              class="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-full"
              :class="WAITING_TONES[trayOldest(activeColumn).tone]"
              :title="t('LEADS.TRIAGE.OLDEST_TITLE')"
            >
              <span class="i-lucide-clock size-3" aria-hidden="true" />
              {{ trayOldestLabel(trayOldest(activeColumn)) }}
            </span>
            <span
              v-else-if="activeColumn.valueSum > 0"
              class="text-xs font-semibold text-n-teal-11"
            >
              {{
                formatLeadCurrency(activeColumn.valueSum, pipeline?.currency)
              }}
            </span>
            <span v-else />
            <div
              v-on-clickaway="() => closeSortMenu(activeColumn.id)"
              class="relative"
            >
              <Button
                ghost
                icon="i-lucide-arrow-up-down"
                size="xs"
                :title="t('LEADS.SORT.TITLE')"
                :class="
                  sortForColumn(activeColumn) !== 'manual'
                    ? 'text-n-brand'
                    : 'text-n-slate-10'
                "
                @click="toggleSortMenu(activeColumn.id)"
              />
              <DropdownMenu
                v-if="sortMenuStageId === activeColumn.id"
                :menu-sections="sortMenuSections(activeColumn)"
                class="right-0 top-7 z-20 min-w-[13rem]"
                @action="onSortAction(activeColumn, $event)"
              />
            </div>
          </div>
          <LeadCard
            v-for="lead in activeColumn.leads"
            :key="lead.id"
            :lead="lead"
            movable
            :triage="isTriageColumn(activeColumn)"
            @open="openConversation"
            @edit="openEditLead"
            @move="openMoveDialog"
            @accept="acceptLead"
            @archive="confirmArchiveLead"
          />
          <p
            v-if="!activeColumn.leads.length"
            class="py-6 text-sm text-center text-n-slate-10"
          >
            {{ emptyColumnLabel(activeColumn) }}
          </p>
          <Button
            v-if="activeColumn.hasMore"
            ghost
            color="slate"
            size="sm"
            :is-loading="activeColumn.loadingMore"
            :label="
              t('LEADS.LOAD_MORE', {
                count: activeColumn.count - activeColumn.leads.length,
              })
            "
            @click="loadColumnLeads(activeColumn, false)"
          />
        </div>
      </div>
    </template>

    <!-- Picker de etapa para mover un lead (móvil): TODAS las etapas (no solo
         las columnas visibles), agrupadas Embudo / Cierre — la única vía de
         cierre del tablero en móvil (no hay drag). -->
    <Dialog ref="moveDialog" :title="t('LEADS.MOVE_TITLE')">
      <div class="flex flex-col gap-1">
        <button
          v-for="stage in movePickerGroups.funnel"
          :key="stage.id"
          class="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-n-alpha-2 disabled:opacity-40"
          :disabled="leadToMove && stage.id === leadToMove.stage_id"
          @click="moveLeadToStage(stage.id)"
        >
          <span
            v-if="stage.stage_type === 'incoming'"
            class="size-3 text-n-slate-10"
            :class="stageTypeIcon(stage.stage_type)"
          />
          <span
            v-else
            class="rounded-full size-2"
            :style="{ backgroundColor: stage.color || '#94A3B8' }"
          />
          {{ stage.name }}
        </button>
        <template v-if="movePickerGroups.closed.length">
          <p
            class="px-3 pt-2 pb-1 text-xs font-medium uppercase text-n-slate-10"
          >
            {{ t('LEADS.STAGE_TYPES.CLOSED_GROUP') }}
          </p>
          <button
            v-for="stage in movePickerGroups.closed"
            :key="stage.id"
            class="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-n-alpha-2 disabled:opacity-40"
            :disabled="leadToMove && stage.id === leadToMove.stage_id"
            @click="moveLeadToStage(stage.id)"
          >
            <span
              class="size-3 text-n-slate-10"
              :class="stageTypeIcon(stage.stage_type)"
            />
            {{ stage.name }}
          </button>
        </template>
      </div>
      <template #footer><span /></template>
    </Dialog>

    <!-- Modal de filtros (móvil): agente + estado + exportar/limpiar -->
    <Dialog
      ref="filtersDialog"
      :title="t('LEADS.FILTER.TITLE')"
      :confirm-button-label="t('LEADS.FILTER.DONE')"
      @confirm="filtersDialog.close()"
    >
      <div class="flex flex-col gap-4">
        <label v-if="hasFullLeadAccess" class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LEADS.FILTER.AGENT') }}
          </span>
          <Select
            v-model="assigneeFilter"
            class="w-full"
            :options="assigneeOptions"
          />
        </label>
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LEADS.FILTER.STATUS') }}
          </span>
          <Select
            v-model="statusFilter"
            class="w-full"
            :options="statusOptions"
          />
        </label>
        <Button
          slate
          faded
          icon="i-lucide-download"
          :label="t('LEADS.EXPORT.LABEL')"
          :is-loading="isExporting"
          @click="exportLeads"
        />
        <Button
          v-if="hasActiveFilters"
          link
          :label="t('LEADS.FILTER.CLEAR')"
          @click="clearFilters"
        />
      </div>
    </Dialog>

    <!-- Confirmar archivado desde el tablero (mismas claves que la ficha). -->
    <Dialog
      ref="boardArchiveDialog"
      type="alert"
      :title="t('LEADS.ARCHIVE.CONFIRM_TITLE')"
      :description="t('LEADS.ARCHIVE.CONFIRM_DESCRIPTION')"
      :confirm-button-label="t('LEADS.ARCHIVE.ARCHIVE')"
      :is-loading="isBoardArchiving"
      :show-cancel-button="!isBoardArchiving"
      @confirm="archiveLeadFromBoard"
    />

    <Dialog
      ref="deleteStageDialog"
      type="alert"
      :title="t('LEADS.STAGE.DELETE_TITLE')"
      :description="deleteStageDescription"
      :confirm-button-label="t('LEADS.STAGE.DELETE')"
      @confirm="handleDeleteStage"
    />

    <Dialog
      ref="pipelineDialog"
      :title="
        pipelineDialogMode === 'create'
          ? t('LEADS.PIPELINE.NEW')
          : t('LEADS.PIPELINE.RENAME')
      "
      :confirm-button-label="t('LEADS.PIPELINE.SAVE')"
      @confirm="submitPipelineDialog"
    >
      <Input
        v-model="pipelineNameInput"
        :label="t('LEADS.PIPELINE.NAME_LABEL')"
        :placeholder="t('LEADS.PIPELINE.NAME_PLACEHOLDER')"
      />
    </Dialog>

    <Dialog
      ref="deletePipelineDialog"
      type="alert"
      :title="t('LEADS.PIPELINE.DELETE_TITLE')"
      :description="t('LEADS.PIPELINE.DELETE_CONFIRM')"
      :confirm-button-label="t('LEADS.PIPELINE.DELETE')"
      @confirm="handleDeletePipeline"
    />

    <!-- Modal de cierre: motivo al perder / valor final al ganar. -->
    <CloseLeadDialog
      ref="closeDialog"
      :reasons="pipeline?.loss_reasons || []"
      @confirm="onCloseConfirm"
      @close="onCloseCancel"
    />

    <!-- Tipo de etapa (admin): retipar con vista previa de impacto. -->
    <StageTypeDialog
      v-if="pipeline"
      ref="stageTypeDialog"
      :pipeline-id="pipeline.id"
      :stages="stages"
      @updated="onStageTypeUpdated"
    />

    <!-- Motivos de pérdida del pipeline (admin). -->
    <LossReasonsDialog
      v-if="pipeline"
      ref="lossReasonsDialog"
      :pipeline="pipeline"
      @updated="onLossReasonsUpdated"
    />
  </div>
</template>
